"use client";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Settings2,
  Handshake,
  FileText,
  LockKeyhole,
  CircleCheckBig,
} from "lucide-react";

interface FeatureItem {
  title: string;
  description: string;
}

interface TabData {
  id: string;
  label: string;
  icon?: "settings" | "file" | "handshake" | "lock";
  features: FeatureItem[];
}

interface CapabilitiesTabsProps {
  title?: string;
  description?: string;
  titleTag?: "h1" | "h2" | "h3";
  titleClassName?: string;
  defaultTab?: string;
  tabs?: TabData[];
}

const iconMap = {
  settings: Settings2,
  file: FileText,
  handshake: Handshake,
  lock: LockKeyhole,
} as const;

function getTabIcon(icon?: TabData["icon"]) {
  const normalized = (icon ?? "settings")
    .trim()
    .toLowerCase() as keyof typeof iconMap;
  return iconMap[normalized] ?? Settings2;
}

export function CapabilitiesTabs({
  title,
  description,
  titleTag = "h2",
  titleClassName = "heading-lg",
  defaultTab,
  tabs,
}: CapabilitiesTabsProps) {
  const TitleTag = titleTag;

  return (
    <section className="bg-background section-padding-y">
      <div className="container container-padding-x relative mx-auto">
        <div className="section-title-gap-lg mb-10 flex max-w-3xl flex-col">
          <TitleTag className={`${titleClassName} text-foreground`}>
            {title}
          </TitleTag>
          {description ? (
            <p className={`${titleClassName} text-muted-foreground`}>
              {description}
            </p>
          ) : null}
        </div>
        <Tabs
          defaultValue={defaultTab || tabs?.[0]?.id}
          className="relative flex w-full flex-col items-start gap-8 lg:flex-row lg:gap-12"
        >
          <TabsList className="flex h-auto w-full flex-col lg:max-w-sm">
            {tabs?.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex w-full items-center justify-start gap-3 p-2 text-base md:p-3"
              >
                {(() => {
                  const Icon = getTabIcon(tab.icon);
                  return (
                    <Icon
                      className="text-current h-6 w-6 shrink-0"
                      aria-hidden="true"
                    />
                  );
                })()}
                <span className="text-left text-pretty font-geist">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {tabs?.map((tab) => (
            <TabsContent key={tab.id} value={tab.id} className="group w-full mt-0">
              <div className="bg-[#0D0F33] space-y-8 rounded-md p-6 md:p-8">
                <h2 className="text-white heading-md group-data-[state=active]:animate-in group-data-[state=active]:fade-in-0 group-data-[state=active]:duration-200 group-data-[state=inactive]:animate-out group-data-[state=inactive]:fade-out-0 group-data-[state=inactive]:duration-200">
                  {tab.label}
                </h2>

                <div className="space-y-8">
                  {tab.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex gap-4 group-data-[state=active]:animate-in group-data-[state=active]:fade-in-0 group-data-[state=active]:duration-200 group-data-[state=inactive]:animate-out group-data-[state=inactive]:fade-out-0 group-data-[state=inactive]:duration-200"
                    >
                      <CircleCheckBig className="text-white h-6 w-6 shrink-0" />
                      <div className="space-y-3">
                        <h3 className="text-white text-base leading-6 font-semibold">
                          {feature.title}
                        </h3>
                        <p className="text-white/72 text-base">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}
